import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalDocument.css";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <main className="legal-page">
      <button type="button" className="legal-back-button" onClick={() => navigate(-1)}>
        Back
      </button>

      <header className="legal-header">
        <p className="legal-kicker">Legal</p>
        <h1>BlueWire Terms of Service</h1>
        <p className="legal-effective"><strong>Effective Date:</strong> 09/25/2025</p>
      </header>

      <section className="legal-section">
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing or using BlueWire, you agree to be bound by these Terms of Service.</p>
      </section>

      <section className="legal-section">
        <h2>2. Eligibility and Verification</h2>
        <p>
          BlueWire is intended exclusively for verified law enforcement personnel
          and authorized public safety professionals who are 21 years of age or older.
        </p>
        <p>
          To access BlueWire, users are required to complete a registration process,
          which may include submission of a valid department-issued email address,
          a department-issued identification credential, and a real-time selfie for
          identity confirmation.
        </p>
        <p>By registering, you represent and warrant that:</p>
        <ul>
          <li>You are affiliated with a legitimate law enforcement or public safety agency.</li>
          <li>All information and documents submitted for verification are accurate and authentic.</li>
          <li>You are the rightful owner of the credentials and identification submitted.</li>
        </ul>
        <p>
          BlueWire reserves the right to approve, deny, or revoke access at its sole
          discretion if verification cannot be confirmed or if fraudulent or misleading
          information is detected.
        </p>
      </section>

      <section className="legal-section">
        <h2>3. User Accounts</h2>
        <p>
          To use BlueWire, you must register an account and provide accurate information
          including your name, work email address, phone number, agency, rank, and assignment.
          You agree to maintain accurate and up-to-date information at all times.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your login credentials
          and for all activity under your account.
        </p>
      </section>

      <section className="legal-section">
        <h2>4. Identity Verification and Security</h2>
        <p>
          BlueWire uses identity verification measures to maintain the integrity and security
          of the platform. These measures may include automated and manual review of submitted
          identification, facial comparison technologies, and verification of agency affiliation.
        </p>
        <p>
          By using BlueWire, you consent to these verification processes. Verification data is
          used solely for authentication and security purposes and is not sold or used for
          commercial advertising.
        </p>
        <p>
          BlueWire reserves the right to re-verify users at any time, request additional
          documentation, or suspend access if verification standards are no longer met.
        </p>
      </section>

      <section className="legal-section">
        <h2>5. User Conduct</h2>
        <p>When using BlueWire, you agree that you will not:</p>
        <ul>
          <li>Provide false or misleading information.</li>
          <li>Impersonate another user or individual.</li>
          <li>Use the platform for any unlawful purpose.</li>
          <li>Attempt to bypass or manipulate the identity verification process.</li>
          <li>Share or distribute sensitive information outside authorized law enforcement use.</li>
          <li>Attempt to disrupt or compromise the application's security.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>6. Content</h2>
        <p>
          Users may create and share notes, BOLOs, and messages. You retain responsibility
          for any content you submit. BlueWire does not sell or share user data for commercial
          purposes, and shared content is visible only to other authenticated users as intended.
        </p>
      </section>

      <section className="legal-section">
        <h2>7. Private Profile Notes</h2>
        <p>
          BlueWire may allow users to create private notes on another user's profile for
          personal reference. These notes are intended to help users remember professional
          context, prior interactions, training history, conversations, or other details
          relevant to future communication.
        </p>
        <p>
          Private Profile Notes are visible only to the user who created them within the app.
          The user whose profile the note is attached to cannot view the note, and other users
          cannot view the note.
        </p>
        <p>
          Users are responsible for the content they enter in Private Profile Notes. Private
          Profile Notes may not be used to store unlawful, threatening, harassing, defamatory,
          discriminatory, or otherwise prohibited content. Users should not include sensitive
          personal information, confidential law enforcement information, criminal justice
          information, medical information, or other restricted information unless they are
          legally authorized to do so.
        </p>
        <p>
          BlueWire may access, preserve, review, disclose, or remove Private Profile Notes
          when necessary to operate the service, provide support, investigate misuse, enforce
          these Terms, protect users or the public, comply with legal obligations, or respond
          to valid legal process.
        </p>
      </section>

      <section className="legal-section">
        <h2>8. News and Third-Party Content</h2>
        <p>
          The News page may display or aggregate articles, headlines, excerpts, images, links,
          and other content from third-party publishers and publicly available sources. BlueWire
          does not own, create, control, or endorse third-party news content, and all such content
          remains the property and responsibility of its original publisher or source.
        </p>
        <p>
          News content may be filtered, ranked, selected, or organized for law enforcement
          relevance; however, BlueWire does not guarantee the accuracy, completeness, timeliness,
          availability, or reliability of any third-party news content. Users should review the
          original publisher's article and source link for the complete and most current information.
        </p>
        <p>
          BlueWire may remove, hide, restrict, or disable access to news content at its discretion,
          including content that is outdated, unavailable, irrelevant, reported, or otherwise
          unsuitable for the platform.
        </p>
        <p>
          Any comments, replies, reactions, or discussions posted by users on news articles are
          user-generated content. Users are responsible for the content they post and must not post
          unlawful, abusive, threatening, harassing, defamatory, obscene, discriminatory, or otherwise
          objectionable material. BlueWire may review, moderate, remove, restrict, or report
          user-generated content at its discretion and may suspend or terminate accounts that violate
          these Terms or applicable law.
        </p>
        <p>
          Users may report objectionable content or abusive users through the app's reporting tools
          or by contacting BlueWire support.
        </p>
      </section>

      <section className="legal-section">
        <h2>9. Data Retention and Deletion</h2>
        <p>
          BlueWire retains user data only as long as necessary to operate the platform and maintain
          security.
        </p>
        <p>
          Verification images, including identification documents and selfies, are stored temporarily
          and are automatically deleted within a limited period after verification is completed.
        </p>
        <p>
          Verification metadata, such as confirmation status and confidence scores, may be retained
          for a limited period for auditing and security purposes.
        </p>
        <p>
          Deleting your account will remove your personal profile information. Certain shared content
          (such as messages or notes) may remain visible to other users if it has already been
          distributed within the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>10. No Guarantee of Identity</h2>
        <p>
          While BlueWire implements identity verification measures, we do not guarantee the identity,
          credentials, or conduct of any user. Users are responsible for exercising their own judgment
          when interacting with others on the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>11. Appropriate Use</h2>
        <p>
          BlueWire is intended to support professional communication and information sharing among
          authorized users. The platform is not a substitute for official law enforcement systems,
          databases, or dispatch channels.
        </p>
        <p>
          Users are responsible for complying with all applicable laws, agency policies, and
          regulations when using BlueWire.
        </p>
      </section>

      <section className="legal-section">
        <h2>12. Termination</h2>
        <p>
          BlueWire reserves the right to suspend or terminate accounts that violate these Terms of
          Service or compromise the security of the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>13. Disclaimer of Warranties</h2>
        <p>
          BlueWire is provided on an "as is" and "as available" basis. We make no warranties or
          representations regarding the reliability, availability, or accuracy of the application.
        </p>
      </section>

      <section className="legal-section">
        <h2>14. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, BlueWire shall not be liable for any indirect,
          incidental, or consequential damages resulting from your use of the application.
        </p>
      </section>

      <section className="legal-section">
        <h2>15. Changes to These Terms</h2>
        <p>
          We may update these Terms of Service from time to time. Any changes will be posted at this
          URL, and continued use of BlueWire constitutes acceptance of the updated terms.
        </p>
      </section>

      <section className="legal-section">
        <h2>16. Contact</h2>
        <p>
          For questions regarding these Terms of Service, please contact us at:{" "}
          <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>.
        </p>
      </section>
    </main>
  );
}
